import { create } from "zustand";
import type { ExpectedDebitsTable } from "../types";
import { getApi } from "../api";
import { reportError } from "../services/eventBus";

/**
 * Catalog state that outlives one screen: the expected-debits tables. Nothing else is cached client-side; every condition
 * and rating screen reads what it shows through the API. The mockup phase cached the whole catalog here, an 8 MB call the
 * app no longer makes.
 */
interface CatalogState {
  expectedTables: readonly ExpectedDebitsTable[] | null;
  isLoadingTables: boolean;
  tablesError: string | null;
  ensureExpectedTables: () => Promise<readonly ExpectedDebitsTable[] | null>;
  setExpectedTables: (tables: readonly ExpectedDebitsTable[]) => void;
}

export const useCatalogStore = create<CatalogState>()((set, get) => ({
  expectedTables: null,
  isLoadingTables: false,
  tablesError: null,

  ensureExpectedTables: async () => {
    const existing = get().expectedTables;
    if (existing) return existing;
    set({ isLoadingTables: true, tablesError: null });
    try {
      const tables = await getApi().listExpectedDebitTables();
      set({ expectedTables: tables, isLoadingTables: false });
      return tables;
    } catch (error) {
      const reported = reportError(error, "catalogStore.ensureExpectedTables");
      set({ isLoadingTables: false, tablesError: reported.message });
      return null;
    }
  },

  setExpectedTables: (tables) => set({ expectedTables: tables }),
}));
